/**
 * Thin fetch client for the LangBridge backend. Ported from the reference
 * console (`langbridge/chat-ui/src/api.ts`). Discovery/IO endpoints are guarded
 * by `x-chat-password`; run/session/abort are open (CORS is `*`). One
 * conversation's streaming run lives in AguiChatClient (@langbridge-builder/chat-ui/agui).
 */
import type { Attachment } from '@langbridge-builder/design-system';
import type {
  AgentBundle,
  AgentIo,
  AgentSummary,
  ProvisionStatus,
  SessionListItem,
  TranscriptMessage,
} from './types';

export const CHAT_PASSWORD_HEADER = 'x-chat-password';

export interface LangbridgeConfig {
  /** Backend origin, e.g. 'http://localhost:3001'. Same-origin ('') is allowed. */
  apiBase: string;
  /** Chat console password (default backend value is 'laivdata'). */
  password?: string;
  /**
   * Central storage server for attachment uploads. On desktop this differs from
   * `apiBase` (the local sidecar has no S3 credentials) — the Electron shell
   * injects the central builder backend here. Unset on web → the upload util
   * falls back to `apiBase` (same server, presigned PUT). See `upload.ts`.
   */
  uploadBase?: string;
  /**
   * Chat password for `uploadBase` (the central server). Unset on web → falls
   * back to `password`. Never a hardcoded secret — reuses the provisioning
   * credential the desktop already holds.
   */
  uploadPassword?: string;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function base(cfg: LangbridgeConfig): string {
  return cfg.apiBase.replace(/\/$/, '');
}

/** Auth header for guarded endpoints. Harmless on open ones (CORS allows it). */
export function authHeaders(cfg: LangbridgeConfig): Record<string, string> {
  return cfg.password ? { [CHAT_PASSWORD_HEADER]: cfg.password } : {};
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(
      res.status,
      `${res.status} ${res.statusText}${body ? ` — ${body.slice(0, 300)}` : ''}`,
    );
  }
  return (await res.json()) as T;
}

/** Verify the chat password against `POST /chat/login`. */
export async function chatLogin(cfg: LangbridgeConfig): Promise<boolean> {
  const res = await fetch(`${base(cfg)}/chat/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password: cfg.password ?? '' }),
  });
  if (res.ok) return true;
  if (res.status === 401) return false;
  const body = await res.text().catch(() => '');
  throw new ApiError(res.status, `${res.status} ${res.statusText}${body ? ` — ${body.slice(0, 300)}` : ''}`);
}

export async function listAgents(cfg: LangbridgeConfig, signal?: AbortSignal): Promise<AgentSummary[]> {
  const res = await fetch(`${base(cfg)}/agents`, { signal, headers: authHeaders(cfg) });
  return jsonOrThrow<AgentSummary[]>(res);
}

export async function getAgentIo(
  cfg: LangbridgeConfig,
  agentId: string,
  signal?: AbortSignal,
): Promise<AgentIo> {
  const res = await fetch(`${base(cfg)}/agents/${encodeURIComponent(agentId)}/io`, {
    signal,
    headers: authHeaders(cfg),
  });
  return jsonOrThrow<AgentIo>(res);
}

/**
 * Export an agent's full dependency graph as a portable bundle
 * (`GET /agents/:id/export`, guarded). The builder captures this at package time
 * and writes it to the desktop app's `agent-bundle.json` (embedded via
 * `PROVISION_BUNDLE_PATH`). Run against the central backend (the source of the
 * chosen chatbot), not the local sidecar.
 */
export async function exportAgentBundle(
  cfg: LangbridgeConfig,
  agentId: string,
  signal?: AbortSignal,
): Promise<AgentBundle> {
  const res = await fetch(`${base(cfg)}/agents/${encodeURIComponent(agentId)}/export`, {
    signal,
    headers: authHeaders(cfg),
  });
  return jsonOrThrow<AgentBundle>(res);
}

export async function createSession(cfg: LangbridgeConfig, agentId: string): Promise<string> {
  const res = await fetch(`${base(cfg)}/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(cfg) },
    body: JSON.stringify({ agentId }),
  });
  const data = await jsonOrThrow<{ sessionId: string }>(res);
  return data.sessionId;
}

/** Best-effort abort. The SSE connection close already tears the run down; this
 * is a belt-and-suspenders call and never throws. */
export async function abortRun(
  cfg: LangbridgeConfig,
  sessionId: string,
  reason = 'user_abort',
): Promise<boolean> {
  try {
    const res = await fetch(`${base(cfg)}/agents/abort`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders(cfg) },
      body: JSON.stringify({ sessionId, reason }),
    });
    const data = await jsonOrThrow<{ accepted: boolean }>(res);
    return data.accepted;
  } catch {
    return false;
  }
}

/** Liveness probe (`GET /health`, 200). Process up; says nothing about the DB. Never throws. */
export async function getHealth(cfg: LangbridgeConfig, signal?: AbortSignal): Promise<boolean> {
  try {
    const res = await fetch(`${base(cfg)}/health`, { signal, headers: authHeaders(cfg) });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Readiness probe (`GET /ready`, 200 with Mongo connected; 503 otherwise). This is
 * the correct signal that the backend can actually serve — use it for boot polling.
 * Never throws.
 */
export async function getReady(cfg: LangbridgeConfig, signal?: AbortSignal): Promise<boolean> {
  try {
    const res = await fetch(`${base(cfg)}/ready`, { signal, headers: authHeaders(cfg) });
    return res.ok;
  } catch {
    return false;
  }
}

export interface WaitForReadyOptions {
  signal?: AbortSignal;
  /** Poll interval between attempts, ms (default 500). */
  intervalMs?: number;
  /** Give up after this many ms (default 60000). */
  timeoutMs?: number;
}

/**
 * Poll `GET /ready` until the bundled local backend can serve (process up + Mongo
 * connected). Used by the desktop shell splash while langbridge boots. Resolves
 * true once ready, false on timeout/abort. Sequential so a slow boot doesn't fan out.
 */
export async function waitForReady(
  cfg: LangbridgeConfig,
  { signal, intervalMs = 500, timeoutMs = 60_000 }: WaitForReadyOptions = {},
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (signal?.aborted) return false;
    if (await getReady(cfg, signal)) return true;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return false;
}

/**
 * Provisioning status (`GET /provision/status`, guarded). Reports whether the
 * desktop sidecar has finished importing its baked agent bundle into local Mongo.
 * The boot import is non-blocking, so this flips `provisioned:false` (importing)
 * → `true` (ready) after `/ready` is already up. Never throws (returns
 * `provisioned:false` on network/parse error, e.g. a backend without the route).
 */
export async function getProvisionStatus(
  cfg: LangbridgeConfig,
  signal?: AbortSignal,
): Promise<ProvisionStatus> {
  try {
    const res = await fetch(`${base(cfg)}/provision/status`, { signal, headers: authHeaders(cfg) });
    if (!res.ok) return { provisioned: false };
    return (await res.json()) as ProvisionStatus;
  } catch {
    return { provisioned: false };
  }
}

export interface WaitForProvisionedOptions {
  signal?: AbortSignal;
  /** Poll interval between attempts, ms (default 500). */
  intervalMs?: number;
  /** Give up after this many ms (default 60000). */
  timeoutMs?: number;
}

/**
 * Poll `GET /provision/status` until the sidecar reports `provisioned:true`
 * (bundle import complete). Used by the desktop shell splash after `/ready`.
 * Resolves the ready status once provisioned, or null on timeout/abort.
 */
export async function waitForProvisioned(
  cfg: LangbridgeConfig,
  { signal, intervalMs = 500, timeoutMs = 60_000 }: WaitForProvisionedOptions = {},
): Promise<ProvisionStatus | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (signal?.aborted) return null;
    const status = await getProvisionStatus(cfg, signal);
    if (status.provisioned) return status;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}

/**
 * List a single agent's past sessions for the history sidebar
 * (`GET /sessions?agentId=`). Newest first (backend-sorted). Guarded endpoint.
 * NOTE: lands with backend request "A"; the contract is agreed but not yet deployed.
 */
export async function listSessions(
  cfg: LangbridgeConfig,
  agentId: string,
  signal?: AbortSignal,
): Promise<SessionListItem[]> {
  const res = await fetch(`${base(cfg)}/sessions?agentId=${encodeURIComponent(agentId)}`, {
    signal,
    headers: authHeaders(cfg),
  });
  return jsonOrThrow<SessionListItem[]>(res);
}

/**
 * Restore a past session's transcript for read-only history rendering
 * (`GET /sessions/:id/messages`). Excludes the heavy checkpoint blob.
 */
export async function getSessionTranscript(
  cfg: LangbridgeConfig,
  sessionId: string,
  signal?: AbortSignal,
): Promise<TranscriptMessage[]> {
  const res = await fetch(`${base(cfg)}/sessions/${encodeURIComponent(sessionId)}/messages`, {
    signal,
    headers: authHeaders(cfg),
  });
  const data = await jsonOrThrow<{ messages: TranscriptMessage[] }>(res);
  return data.messages ?? [];
}

/** Delete a session (transcript + graph checkpoints). Never throws. */
export async function deleteSession(cfg: LangbridgeConfig, sessionId: string): Promise<boolean> {
  try {
    const res = await fetch(`${base(cfg)}/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      headers: authHeaders(cfg),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** dot-path (`a.b.c`) set. Mirrors the server's quickRun mapping (no array index). */
function setPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i]!;
    if (typeof cur[p] !== 'object' || cur[p] === null) cur[p] = {};
    cur = cur[p] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]!] = value;
}

/**
 * Build the agent's query object by injecting the user's text into every
 * `userInputKeys` entry (falling back to `fallbackKey`/'query'). Matches the
 * server's quickRun mapping so history/start variables receive the input
 * regardless of the agent's configured input key.
 */
export function buildQuery(io: AgentIo, text: string): Record<string, unknown> {
  const obj: Record<string, unknown> = {};
  const keys = io.input.userInputKeys.length
    ? io.input.userInputKeys
    : [io.input.fallbackKey || 'query'];
  for (const key of keys) setPath(obj, key, text);
  return obj;
}

/** Wire-level multimodal part (matches the backend's MultimodalInput shape). */
interface MultimodalPart {
  type: string; // image | video | audio | file
  url?: string;
  base64?: string; // raw, no data: prefix
  mimeType?: string;
}

function toPart(a: Attachment): MultimodalPart {
  return a.base64
    ? { type: a.kind, base64: a.base64, mimeType: a.mimeType }
    : { type: a.kind, url: a.url, mimeType: a.mimeType };
}

/**
 * True if this agent accepts attachments. Prefers the backend-reported
 * `acceptsAttachments`; falls back (older backends) to type/keys: deep_agent and
 * react always do (inline), workflow only with a multimodal key.
 */
export function acceptsAttachments(io: AgentIo): boolean {
  if (typeof io.input.acceptsAttachments === 'boolean') return io.input.acceptsAttachments;
  if (io.type === 'deep_agent' || io.type === 'reasoning_and_act') return true;
  return io.input.multimodalKeys.length > 0;
}

/**
 * Composer attachment capability derived from the agent IO. `mode` is `'url'`
 * only for workflow agents whose multimodal key is `image_url` (URL-only);
 * everything else (deep/react/workflow-multimodal) is `'file'` (inline upload).
 */
export function attachCapability(
  io: AgentIo | null,
): { accept: boolean; mode: 'file' | 'url'; mimeTypes?: string[] } {
  if (!io) return { accept: false, mode: 'file' };
  const mmKey = io.input.multimodalKeys[0];
  const urlOnly =
    io.type !== 'deep_agent' && io.type !== 'reasoning_and_act' && mmKey?.mode === 'image_url';
  return {
    accept: acceptsAttachments(io),
    mode: urlOnly ? 'url' : 'file',
    mimeTypes: io.input.supportedMimeTypes,
  };
}

/**
 * Build the agent's query object with the user's text AND multimodal attachments,
 * placed per the agent's IO contract (see the backend `forwardedProps.query`
 * passthrough):
 *   - deep_agent / react → reserved `__multimodalParts` carry key (works even
 *     with no multimodal key; the backend assembles the human turn / FS offload).
 *   - workflow + `image_url` key → array of URLs (base64 → data: URL, since we
 *     bypass the server's inject conversion).
 *   - workflow + `multimodal` key → array of MultimodalInput parts at the key.
 *   - otherwise → attachments dropped (agent can't consume them).
 */
export function buildRunQuery(
  io: AgentIo,
  text: string,
  attachments?: Attachment[],
): Record<string, unknown> {
  const query = buildQuery(io, text);
  if (!attachments?.length || !acceptsAttachments(io)) return query;

  if (io.type === 'deep_agent' || io.type === 'reasoning_and_act') {
    query.__multimodalParts = attachments.map(toPart);
    return query;
  }

  const mmKey = io.input.multimodalKeys[0];
  if (!mmKey) return query;
  if (mmKey.mode === 'image_url') {
    const urls = attachments
      .map((a) =>
        a.url ??
        (a.base64 ? `data:${a.mimeType ?? 'application/octet-stream'};base64,${a.base64}` : undefined),
      )
      .filter((u): u is string => !!u);
    if (urls.length) setPath(query, mmKey.key, urls);
  } else {
    setPath(query, mmKey.key, attachments.map(toPart));
  }
  return query;
}
